import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { PageMetadata } from '@/types/page'
import { apiData, categories } from '@/data/apiReference'

export const metadata: PageMetadata = {
  id: 'api-reference',
  category: 'reference',
  categoryName: 'Reference',
  name: 'API Reference',
  icon: '📚',
  path: '/api-reference',
  title: 'Zendesk Web Widget API Reference',
  description: 'Complete reference of all implemented Zendesk Web Widget APIs'
}

export default function ApiReference() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')

  useEffect(() => {
    document.title = `Internal Note - ${metadata.title}`
  }, [])

  const filteredAPIs = apiData.filter(api => {
    const matchesSearch =
      api.method.toLowerCase().includes(searchTerm.toLowerCase()) ||
      api.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      api.parameters.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesCategory = selectedCategory === 'All' || api.category === selectedCategory

    return matchesSearch && matchesCategory
  })

  const implementedCount = apiData.filter(api => api.status === 'implemented').length
  const totalCount = apiData.length

  return (
    <>
      <header>
        <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 md:px-8">
          <div className="md:flex md:items-center md:justify-between md:space-x-5">
            <div>
              <h1 className="text-3xl text-licorice dark:text-white">{metadata.title}</h1>
              <h2 className="mt-2 text-xl text-licorice dark:text-white">{metadata.description}</h2>
            </div>
          </div>
        </div>
      </header>

      <div className="mt-8 mb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {/* Stats */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-licorice dark:text-white mb-2">Implementation Status</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This demo site implements <strong className="text-green-700 dark:text-green-300">{implementedCount} out of {totalCount}</strong> documented Zendesk Web Widget APIs
                </p>
              </div>
              <div className="text-5xl">
                {implementedCount === totalCount ? '🎉' : '✅'}
              </div>
            </div>
            <div className="mt-4 bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
              <div
                className="bg-green-500 h-full transition-all duration-500"
                style={{ width: `${(implementedCount / totalCount) * 100}%` }}
              />
            </div>
          </div>

          {/* Search and Filter */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Search */}
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-licorice dark:text-white mb-2">
                  Search APIs
                </label>
                <input
                  id="search"
                  type="text"
                  placeholder="Search by method, description, or parameters..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-licorice dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Category Filter */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-licorice dark:text-white mb-2">
                  Filter by Category
                </label>
                <select
                  id="category"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-licorice dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results count */}
            <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              Showing {filteredAPIs.length} of {totalCount} APIs
            </div>
          </div>

          {/* API Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      API Method
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Parameters
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Demo Page
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredAPIs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        No APIs found matching your search criteria
                      </td>
                    </tr>
                  ) : (
                    filteredAPIs.map((api, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <code className="text-xs bg-gray-100 dark:bg-gray-900 px-2 py-1 rounded text-blue-600 dark:text-blue-400">
                            {api.method}
                          </code>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                            {api.category}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                          {api.description}
                          {api.notes && (
                            <div className="text-xs text-gray-500 dark:text-gray-500 mt-1 italic">
                              {api.notes}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-xs text-gray-500 dark:text-gray-400 max-w-xs">
                          <code className="break-words">{api.parameters}</code>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          {api.demoPagePath ? (
                            <Link
                              to={api.demoPagePath}
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {api.demoPage}
                            </Link>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-600">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {api.status === 'implemented' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                              ✅ Implemented
                            </span>
                          )}
                          {api.status === 'partial' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200">
                              🚧 Partial
                            </span>
                          )}
                          {api.status === 'not-implemented' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200">
                              ❌ Not Yet
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-6 bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-licorice dark:text-white mb-3">Legend</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 mr-2">
                  ✅ Implemented
                </span>
                <span className="text-gray-600 dark:text-gray-400">Fully working demo available</span>
              </div>
              <div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 mr-2">
                  🚧 Partial
                </span>
                <span className="text-gray-600 dark:text-gray-400">Partially demonstrated</span>
              </div>
              <div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 mr-2">
                  ❌ Not Yet
                </span>
                <span className="text-gray-600 dark:text-gray-400">Not yet implemented</span>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h4 className="font-semibold text-licorice dark:text-white mb-2">About This Reference</h4>
            <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2 list-disc list-inside">
              <li>This page provides a complete overview of all Zendesk Web Widget APIs demonstrated on this site</li>
              <li>Click on any demo page link to see the API in action</li>
              <li>Use the search and filter to quickly find specific APIs</li>
              <li>This reference is maintained alongside the codebase for easy future comparisons</li>
              <li>For official documentation, visit <a href="https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/sdk_api_reference/" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline">Zendesk Developer Docs</a></li>
            </ul>
          </div>
        </div>
      </div>
    </>
  )
}
